import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  VpnKey as KeyIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  LinearProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useNotification } from "../contexts/NotificationContext";
import { getProductById, getProductVersions } from "../services/productService";
import { userProductLicenseService } from "../services/userProductLicenseService";
import { themedPrimaryButtonStyle } from "../styles/themeStyles";
import {
  calculateLicenseStatus,
  getDaysUntilExpiry,
} from "../utils/licenseStatusUtils";

const getProductImage = (productName) => {
  if (!productName) return "#FFFFFF";
  const colors = [
    "#FF6B6B",
    "#4ECDC4",
    "#45B7D1",
    "#96CEB4",
    "#FFEAA7",
    "#DDA0DD",
    "#98D8C8",
    "#F7DC6F",
    "#BB8FCE",
    "#85C1E9",
  ];
  const color = colors[productName.charCodeAt(0) % colors.length];
  return color;
};

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [product, setProduct] = useState(null);
  const [versions, setVersions] = useState([]);
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const theme = useTheme();

  useEffect(() => {
    fetchProductDetails();
  }, [productId]);

  const productColor = getProductImage(product?.name);

  const fetchProductDetails = async () => {
    try {
      setLoading(true);

      // Fetch product details
      const productData = await getProductById(productId);
      console.log("Product details fetched:", productData);
      setProduct(productData);

      // Fetch all versions of the product
      const versionsData = await getProductVersions(productId);
      console.log("Versions fetched:", versionsData);

      // Check if any version is marked as latest
      const hasLatest = versionsData.some((v) => v.is_latest);
      console.log("Has latest version:", hasLatest);

      setVersions(versionsData);

      // Fetch license information if product requires license
      if (productData.requires_license && !productData.is_license_server) {
        try {
          const licenseData =
            await userProductLicenseService.getMyProductLicense(productId);
          console.log("License data fetched:", licenseData);
          setLicense(licenseData.license || null);
        } catch (licenseError) {
          console.log("No license found for this product:", licenseError);
          setLicense(null);
        }
      }

      setError("");
    } catch (err) {
      setError("Failed to load product details. Please try again later.");
      console.error("Error fetching product details:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (versionId) => {
    try {
      // Show warning if license is expired or missing
      if (product?.requires_license) {
        if (!license) {
          showNotification(
            "Warning: Downloading without a license. Contact your administrator.",
            "warning",
          );
        } else if (isLicenseExpired(license)) {
          showNotification(
            "Warning: Downloading with an expired license. Contact your administrator for renewal.",
            "warning",
          );
        }
      }

      // First, initiate the download
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/downloads/start`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ versionId }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to start download");
      }

      const data = await response.json();

      // Verify we have a valid download URL
      if (!data.downloadUrl) {
        throw new Error("Invalid download URL received");
      }

      // Create a temporary link and click it
      const link = document.createElement("a");
      link.href = data.downloadUrl;
      link.setAttribute("download", ""); // This will force download
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Download error:", error);
      // Show error message to user
      setError("Failed to download file. Please try again.");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " bytes";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(2) + " KB";
    else if (bytes < 1073741824) return (bytes / 1048576).toFixed(2) + " MB";
    else return (bytes / 1073741824).toFixed(2) + " GB";
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Import and use centralized license status utilities
  const isLicenseExpiring = (license) => {
    if (!license || !license.license_key) return false;
    // Check if license is inactive
    if (license.is_active === false) return false;

    const status = calculateLicenseStatus(license);
    return status === "expiring_soon";
  };

  const isLicenseExpired = (license) => {
    if (!license || !license.license_key) return false;
    const status = calculateLicenseStatus(license);
    return status === "expired";
  };

  const isLicenseInactive = (license) => {
    if (!license) return false;
    return license.is_active === false;
  };

  // Use the centralized getDaysUntilExpiry function
  const getDaysUntilExpiryLocal = (license) => {
    return getDaysUntilExpiry(license);
  };

  const copyLicenseKey = async (licenseKey) => {
    try {
      await navigator.clipboard.writeText(licenseKey);
      if (license && isLicenseExpired(license)) {
        showNotification(
          "License key copied successfully! Note: This license has expired.",
          "warning",
        );
      } else {
        showNotification("License key copied successfully!");
      }
    } catch (err) {
      console.error("Failed to copy license key:", err);
      showNotification("Failed to copy license key", "error");
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: "center" }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading product details...
        </Typography>
      </Container>
    );
  }

  if (!product && !loading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">Product not found or has been removed.</Alert>
        <Box sx={{ mt: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/products")}
          >
            Back to Products
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="xl"
      sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 4, sm: 8 }, px: { xs: 2, sm: 3 } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: { xs: 2, sm: 3 },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            mr: { sm: 2 },
            mb: { xs: 1, sm: 0 },
            alignSelf: { xs: "flex-start", sm: "center" },
          }}
          size={window.innerWidth < 600 ? "small" : "medium"}
        >
          Back
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Product Banner */}
      <Paper
        elevation={3}
        variant="outlined"
        sx={{
          borderRadius: 2,
          mb: { xs: 5, sm: 6 },
        }}
      >
        <Paper
          sx={{
            p: 0,
            borderRadius: "inherit",
            overflow: "hidden",
            position: "relative",
            background: theme.palette.background.paper,
            border: "none",
          }}
        >
          <Box
            sx={{
              position: "relative",
              height: "300px",
              width: "100%",
              background: product.banner_image
                ? "none"
                : `linear-gradient(135deg, ${productColor}, ${alpha(
                    productColor,
                    0.7,
                  )})`,
            }}
          >
            {product?.banner_image ? (
              <img
                src={product.banner_image}
                alt={`${product.name} banner`}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            ) : (
              <>
                <Avatar
                  sx={{
                    width: "100%",
                    height: "100%",
                    fontSize: "2rem",
                    fontWeight: "bold",
                    bgcolor: "rgba(255,255,255,0.2)",
                    color: "common.white",
                  }}
                >
                  <SecurityIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography
                  align="center"
                  sx={{
                    position: "absolute",
                    alignSelf: "center",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: "rgba(0,0,0,0.25)",
                    padding: "20px",
                    fontSize: "20px",
                    color: "#000",
                    fontWeight: "bold",
                  }}
                >
                  No preview image available for this product
                </Typography>
              </>
            )}

            {/* License Status Badge on Banner */}
            {product.requires_license && !product.is_license_server && (
              <Chip
                icon={
                  license ? (
                    isLicenseInactive(license) ? (
                      <WarningIcon />
                    ) : isLicenseExpired(license) ? (
                      <WarningIcon />
                    ) : isLicenseExpiring(license) ? (
                      <ScheduleIcon />
                    ) : (
                      <CheckCircleIcon />
                    )
                  ) : (
                    <KeyIcon />
                  )
                }
                label={
                  license
                    ? isLicenseInactive(license)
                      ? "INACTIVE"
                      : isLicenseExpired(license)
                        ? "EXPIRED"
                        : isLicenseExpiring(license)
                          ? `${getDaysUntilExpiry(license)}d left`
                          : "ACTIVE"
                    : "NO LICENSE"
                }
                color={
                  license
                    ? isLicenseInactive(license)
                      ? "default"
                      : isLicenseExpired(license)
                        ? "error"
                        : isLicenseExpiring(license)
                          ? "warning"
                          : "success"
                    : "error"
                }
                size="medium"
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  zIndex: 2,
                  fontWeight: "bold",
                  fontSize: "0.875rem",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                }}
              />
            )}

            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                top: 0,
                background: `linear-gradient(transparent, ${theme.palette.background.default})`,
                alignContent: "end",
                p: 3,
              }}
            >
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  color: "text.primary",
                  fontWeight: "bold",
                  // textShadow: "2px 2px 4px rgba(0,0,0,0.8)",
                  fontSize: { xs: "1.75rem", sm: "2.125rem" },
                }}
              >
                {product.name}
              </Typography>

              <Typography
                variant="body1"
                paragraph
                color="text.primary"
                sx={{
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  lineHeight: { xs: 1.5, sm: 1.6 },
                  margin: 0,
                }}
              >
                {product?.description ||
                  "No description available for this product."}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* License Information */}
        {product?.requires_license && !product?.is_license_server && (
          <Box sx={{ p: 3, mt: { xs: 3, sm: 4 } }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <KeyIcon color="primary" />
              License Information
            </Typography>

            {license ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 3,
                }}
              >
                {/* License Card */}
                <Box sx={{ flex: 1 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      border: "1px solid",
                      borderColor: isLicenseInactive(license)
                        ? "grey.400"
                        : isLicenseExpired(license)
                          ? "error.main"
                          : isLicenseExpiring(license)
                            ? "warning.main"
                            : "success.main",
                      bgcolor: isLicenseInactive(license)
                        ? "rgba(158, 158, 158, 0.04)"
                        : isLicenseExpired(license)
                          ? "rgba(244, 67, 54, 0.04)"
                          : isLicenseExpiring(license)
                            ? "rgba(255, 152, 0, 0.04)"
                            : "rgba(76, 175, 80, 0.04)",
                      borderRadius: 2,
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 3 }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: isLicenseInactive(license)
                              ? "grey.500"
                              : isLicenseExpired(license)
                                ? "error.main"
                                : isLicenseExpiring(license)
                                  ? "warning.main"
                                  : "success.main",
                            mr: 2,
                            width: 48,
                            height: 48,
                          }}
                        >
                          {isLicenseInactive(license) ? (
                            <WarningIcon />
                          ) : isLicenseExpired(license) ? (
                            <WarningIcon />
                          ) : isLicenseExpiring(license) ? (
                            <ScheduleIcon />
                          ) : (
                            <CheckCircleIcon />
                          )}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            sx={{ mb: 0.5 }}
                          >
                            {isLicenseInactive(license)
                              ? "License Inactive"
                              : isLicenseExpired(license)
                                ? "License Expired"
                                : isLicenseExpiring(license)
                                  ? "License Expiring Soon"
                                  : "License Active"}
                          </Typography>
                          <Chip
                            label={
                              isLicenseInactive(license)
                                ? "INACTIVE"
                                : isLicenseExpired(license)
                                  ? "EXPIRED"
                                  : isLicenseExpiring(license)
                                    ? `${getDaysUntilExpiry(license)} days left`
                                    : "ACTIVE"
                            }
                            color={
                              isLicenseInactive(license)
                                ? "default"
                                : isLicenseExpired(license)
                                  ? "error"
                                  : isLicenseExpiring(license)
                                    ? "warning"
                                    : "success"
                            }
                            size="small"
                            sx={{
                              fontWeight: "bold",
                              fontSize: "0.75rem",
                            }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ wordWrap: "anywhere" }}
                          color="text.secondary"
                          gutterBottom
                        >
                          <strong>License Key:</strong> {license.license_key}
                        </Typography>

                        {license.expiry_date && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            <strong>Expires:</strong>{" "}
                            {formatDate(license.expiry_date)}
                          </Typography>
                        )}

                        {license.hardware_id && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            <strong>Hardware ID:</strong>{" "}
                            {license.hardware_id.substring(0, 20)}...
                          </Typography>
                        )}
                      </Box>

                      {/* Copy License Button inside the card */}
                      <Box sx={{ mt: 2, mb: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<KeyIcon />}
                          onClick={() => copyLicenseKey(license.license_key)}
                          sx={{
                            borderColor: isLicenseInactive(license)
                              ? "grey.500"
                              : isLicenseExpired(license)
                                ? "error.main"
                                : isLicenseExpiring(license)
                                  ? "warning.main"
                                  : "success.main",
                            color: isLicenseInactive(license)
                              ? "grey.700"
                              : isLicenseExpired(license)
                                ? "error.main"
                                : isLicenseExpiring(license)
                                  ? "warning.main"
                                  : "success.main",
                            fontWeight: "medium",
                            fontSize: "0.8rem",
                            "&:hover": {
                              bgcolor: isLicenseInactive(license)
                                ? "rgba(158, 158, 158, 0.08)"
                                : isLicenseExpired(license)
                                  ? "rgba(244, 67, 54, 0.08)"
                                  : isLicenseExpiring(license)
                                    ? "rgba(255, 152, 0, 0.08)"
                                    : "rgba(76, 175, 80, 0.08)",
                            },
                          }}
                        >
                          Copy License Key
                        </Button>
                      </Box>

                      {isLicenseExpiring(license) &&
                        !isLicenseExpired(license) && (
                          <Box sx={{ mt: 2 }}>
                            <Typography
                              variant="caption"
                              display="block"
                              gutterBottom
                              color="text.secondary"
                            >
                              Time until expiry:
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={Math.max(
                                0,
                                Math.min(
                                  100,
                                  (getDaysUntilExpiry(license) / 10) * 100,
                                ),
                              )}
                              color={
                                getDaysUntilExpiry(license) <= 3
                                  ? "error"
                                  : "warning"
                              }
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          </Box>
                        )}

                      {/* Action Buttons */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          minWidth: { xs: "100%", md: "280px" },
                          alignItems: "stretch",
                        }}
                      >
                        {isLicenseInactive(license) && (
                          <Alert severity="warning" sx={{ textAlign: "left" }}>
                            <Typography variant="body2">
                              Your license is inactive. Contact your
                              administrator for assistance.
                            </Typography>
                          </Alert>
                        )}

                        {isLicenseExpired(license) &&
                          !isLicenseInactive(license) && (
                            <Alert severity="error" sx={{ textAlign: "left" }}>
                              <Typography variant="body2">
                                Your license has expired. Contact your
                                administrator for renewal.
                              </Typography>
                            </Alert>
                          )}

                        {isLicenseExpiring(license) &&
                          !isLicenseExpired(license) &&
                          !isLicenseInactive(license) && (
                            <Alert
                              severity="warning"
                              sx={{ textAlign: "left" }}
                            >
                              <Typography variant="body2">
                                Your license expires in{" "}
                                {getDaysUntilExpiry(license)} days. Consider
                                renewing soon.
                              </Typography>
                            </Alert>
                          )}
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: "error.main",
                      mr: 2,
                      width: 48,
                      height: 48,
                    }}
                  >
                    <KeyIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                      No License Found
                    </Typography>
                    <Chip
                      label="NO LICENSE"
                      color="error"
                      size="small"
                      sx={{
                        fontWeight: "bold",
                        fontSize: "0.75rem",
                      }}
                    />
                  </Box>
                </Box>

                <Alert severity="warning">
                  <Typography variant="body2">
                    This product requires a license, but no license was found
                    for your account. Please contact your administrator to
                    obtain a license key.
                  </Typography>
                </Alert>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      <Typography variant="h5" gutterBottom>
        Available Versions
      </Typography>

      {product.is_license_server && versions.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Your downloads are being prepared. You'll receive an email when
          they're ready.
        </Alert>
      )}

      {versions.length === 0 ? (
        <Typography variant="body1" color="text.secondary">
          No versions available for this product yet.
        </Typography>
      ) : (
        <List>
          {versions.map((version, index) => (
            <React.Fragment key={version.id}>
              <ListItem
                alignItems="flex-start"
                sx={{
                  py: 2,
                  pr: { xs: 2, sm: 25 }, // Add right padding to prevent overlap
                  minHeight: 120, // Ensure minimum height for button space
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  borderRadius: 0,
                  marginTop: 2,
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="h6" component="span">
                        Version {version.version}
                      </Typography>
                      {version.is_latest && (
                        <Box
                          component="span"
                          sx={{
                            ml: 2,
                            px: 1,
                            py: 0.5,
                            bgcolor: "primary.main",
                            color: "white",
                            borderRadius: 1,
                            fontSize: "0.75rem",
                            fontWeight: "bold",
                          }}
                        >
                          LATEST
                        </Box>
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Released: {formatDate(version.created_at)}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        File size: {formatFileSize(version.file_size)}
                      </Typography>

                      {version.changelog && (
                        <Card
                          variant="outlined"
                          sx={{ mt: 1, mb: 1, border: "none" }}
                        >
                          <CardContent
                            sx={{ py: 1, "&:last-child": { pb: 1 } }}
                          >
                            <Typography variant="subtitle2" gutterBottom>
                              Changelog:
                            </Typography>
                            <Typography
                              variant="body2"
                              component="div"
                              sx={{ whiteSpace: "pre-wrap" }}
                            >
                              {version.changelog}
                            </Typography>
                          </CardContent>
                        </Card>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction
                  sx={{
                    position: "absolute",
                    transform: "none",
                    top: "8px",
                    right: "8px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 1,
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload(version.id)}
                      sx={{
                        ...themedPrimaryButtonStyle(),
                        whiteSpace: "nowrap",
                        minWidth: 180,
                        py: 1,
                        px: 1,
                        fontSize: "1rem",
                        fontWeight: "bold",
                        ...(product?.requires_license &&
                          !product?.is_license_server &&
                          license &&
                          isLicenseExpired(license) && {
                            bgcolor: "error.main",
                            "&:hover": {
                              bgcolor: "error.dark",
                            },
                          }),
                        ...(product?.requires_license &&
                          !product?.is_license_server &&
                          !license && {
                            bgcolor: "error.main",
                            "&:hover": {
                              bgcolor: "error.dark",
                            },
                          }),
                      }}
                    >
                      {product?.requires_license &&
                      !product?.is_license_server &&
                      license &&
                      isLicenseExpired(license)
                        ? "Download (License Expired)"
                        : product?.requires_license &&
                            !product?.is_license_server &&
                            !license
                          ? "Download (No License)"
                          : `Download v${version.version}`}
                    </Button>
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}
    </Container>
  );
};

export default ProductDetailPage;
